import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type {
  AutoWithdrawalResponse, AutoWithdrawalType, Currency, WalletResponse,
} from '@/shared/api/types'
import { checkAmount, normalizeAmount } from '@/shared/lib/decimal'
import { explain } from '@/shared/lib/errors'
import type { CabinetStore } from '@/stores/CabinetStore'
import type { UiStore } from '@/stores/UiStore'

export type RuleDialog =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; rule: AutoWithdrawalResponse }
  | { kind: 'remove'; rule: AutoWithdrawalResponse }

export interface RuleDraft {
  type: AutoWithdrawalType
  currency: Currency | null
  threshold: string
  triggerTime: string
  timezone: string
  walletId: string | null
  enabled: boolean
}

const EMPTY: RuleDraft = {
  type: 'by_amount', currency: null, threshold: '',
  triggerTime: '12:00', timezone: '', walletId: null, enabled: true,
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * Правила автовывода.
 *
 * Главная опасность экрана — рассогласование валюты и кошелька.
 * Правило «выводить BTC на кошелёк USDT (TRC20)» выглядит в форме
 * совершенно нормально, а срабатывает ночью и без человека: биткоины
 * уходят на TRON-адрес и не возвращаются. Поэтому список кошельков
 * здесь всегда отфильтрован по выбранной валюте, а смена валюты
 * сбрасывает выбранный кошелёк, а не оставляет прежний.
 */
export class AutoWithdrawalsStore {
  dialog: RuleDialog = { kind: 'none' }
  draft: RuleDraft = EMPTY
  errors: Partial<Record<keyof RuleDraft, string>> = {}
  busy = false

  private readonly cabinet: CabinetStore
  private readonly ui: UiStore

  constructor(cabinet: CabinetStore, ui: UiStore) {
    this.cabinet = cabinet
    this.ui = ui
    makeAutoObservable<this, 'cabinet' | 'ui'>(
      this, { cabinet: false, ui: false },
    )
  }

  get items(): AutoWithdrawalResponse[] {
    return this.cabinet.rules.value ?? []
  }

  get wallets(): WalletResponse[] {
    return this.cabinet.wallets.value ?? []
  }

  /** Валюты, под которые вообще есть куда выводить. */
  get availableCurrencies(): Currency[] {
    const seen = new Set<Currency>()
    for (const wallet of this.wallets) seen.add(wallet.currency)
    return [...seen]
  }

  /** Кошельки выбранной валюты — и только они. */
  get walletsForDraft(): WalletResponse[] {
    const currency = this.draft.currency
    if (currency === null) return []
    return this.wallets.filter((wallet) => wallet.currency === currency)
  }

  walletName(id: string): string {
    return this.wallets.find((wallet) => wallet.id === id)?.name ?? 'Кошелёк удалён'
  }

  openAdd(): void {
    this.dialog = { kind: 'add' }
    this.draft = { ...EMPTY, timezone: 'UTC' }
    this.errors = {}
  }

  openEdit(rule: AutoWithdrawalResponse): void {
    this.dialog = { kind: 'edit', rule }
    this.draft = {
      type: rule.type,
      currency: rule.currency,
      threshold: rule.threshold ?? '',
      triggerTime: (rule.trigger_time ?? '12:00').slice(0, 5),
      // Пояс берём ИЗ ПРАВИЛА, а не из профиля: правило создавалось
      // со своим, и подстановка текущего сдвинула бы срабатывание
      // на несколько часов при правке чего-то совсем другого.
      timezone: rule.timezone || 'UTC',
      walletId: rule.wallet_id,
      enabled: rule.enabled,
    }
    this.errors = {}
  }

  openRemove(rule: AutoWithdrawalResponse): void {
    this.dialog = { kind: 'remove', rule }
  }

  close(): void {
    if (this.busy) return
    this.dialog = { kind: 'none' }
    this.errors = {}
  }

  patch(part: Partial<RuleDraft>): void {
    const next = { ...this.draft, ...part }
    // Смена валюты обязана обнулить кошелёк: оставленный от прошлой
    // валюты, он и есть тот самый перевод в чужую сеть.
    if (part.currency !== undefined && part.currency !== this.draft.currency) {
      next.walletId = null
    }
    this.draft = next
    for (const key of Object.keys(part) as (keyof RuleDraft)[]) {
      if (this.errors[key]) {
        const rest = { ...this.errors }
        delete rest[key]
        this.errors = rest
      }
    }
  }

  private validate(): boolean {
    const errors: Partial<Record<keyof RuleDraft, string>> = {}
    const { type, currency, walletId } = this.draft

    if (currency === null) errors.currency = 'Выберите валюту'

    if (type === 'by_amount') {
      const problem = currency === null ? 'Укажите сумму'
        : checkAmount(this.draft.threshold, currency)
      if (problem) errors.threshold = problem
    } else if (!TIME_RE.test(this.draft.triggerTime)) {
      errors.triggerTime = 'Время в формате ЧЧ:ММ'
    }

    if (walletId === null) errors.walletId = 'Выберите кошелёк'
    else {
      // Последний рубеж: кошелёк мог остаться от прошлой валюты,
      // если что-то в форме пойдёт не так. Отправлять такое на
      // бекенд нельзя даже с надеждой, что он проверит сам.
      const wallet = this.wallets.find((w) => w.id === walletId)
      if (!wallet) errors.walletId = 'Кошелёк больше не существует'
      else if (currency !== null && wallet.currency !== currency) {
        errors.walletId = 'Кошелёк другой валюты'
      }
    }

    this.errors = errors
    return Object.keys(errors).length === 0
  }

  async submit(): Promise<void> {
    if (this.busy || !this.validate()) return
    const dialog = this.dialog
    const { type, currency, walletId, enabled } = this.draft
    if (currency === null || walletId === null) return

    // Время правила ВСЕГДА в UTC. Выбора пояса в макете нет, и вместо
    // скрытого «какого-то» пояса лучше один явный: он написан прямо
    // в поле, и «в 12:00» значит одно и то же у всех.
    //
    // На бекенде пояс у правила остался — там он пригодится API, когда
    // до него дойдёт очередь. Кабинет просто всегда шлёт UTC.
    const timezone = 'UTC'

    this.busy = true
    try {
      if (dialog.kind === 'add') {
        await api.money.addAutoWithdrawal({
          type,
          currency,
          wallet_id: walletId,
          enabled,
          ...(type === 'by_amount'
            ? { threshold: normalizeAmount(this.draft.threshold) }
            : { trigger_time: `${this.draft.triggerTime}:00`, timezone }),
        })
      } else if (dialog.kind === 'edit') {
        await api.money.updateAutoWithdrawal(dialog.rule.id, {
          wallet_id: walletId,
          enabled,
          ...(type === 'by_amount'
            ? { threshold: normalizeAmount(this.draft.threshold) }
            : { trigger_time: `${this.draft.triggerTime}:00`, timezone }),
        })
      } else return
      await this.cabinet.rules.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success(dialog.kind === 'add' ? 'Автовывод добавлен' : 'Автовывод изменён')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  /** Быстрый выключатель прямо в списке. */
  async toggle(rule: AutoWithdrawalResponse, enabled: boolean): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await api.money.updateAutoWithdrawal(rule.id, { enabled })
      await this.cabinet.rules.load()
      this.ui.success(enabled ? 'Автовывод включён' : 'Автовывод отключён')
    } catch (cause) {
      this.ui.failure(explain(cause))
      // Переключатель ведомый: если бекенд отказал, список
      // перечитывается и галка возвращается на место сама.
      await this.cabinet.rules.load()
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async remove(): Promise<void> {
    const dialog = this.dialog
    if (dialog.kind !== 'remove' || this.busy) return
    this.busy = true
    try {
      await api.money.deleteAutoWithdrawal(dialog.rule.id)
      await this.cabinet.rules.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Автовывод удалён')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }
}
